import { Component, ElementRef } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { PxErrorFormControl, PxErrorItem } from '../../models';

export interface IPxErrorBoxComponent {
    validate(formGroup: FormGroup, withScrolling?: boolean): void;

    reset(): void;
}

@Component({
    selector: 'px-error-box',
    templateUrl: './error-box.component.html',
    styleUrls: ['./error-box.component.scss'],
})
export class ErrorBoxComponent implements IPxErrorBoxComponent {
    errors: PxErrorItem[] = [];

    constructor(private hostElement: ElementRef) {}

    static findTargetElementInShell(shell) {
        let targetElement = shell.querySelector(`label[for]`);
        // if not exists search for input
        if (!targetElement) {
            targetElement = shell.querySelector(`[id$="_inp"]`);
        }
        if (!targetElement) {
            targetElement = shell.querySelector(`[id$="_upload_btn"]`);
        }
        return targetElement;
    }

    static findLabelInShell(shell) {
        // find the label with the data-attribute label
        let label = shell.getAttribute('data-label');
        if (label) {
            return label;
        }
        if (!label) {
            // find the label
            label = shell.querySelector(`label`);
        }
        // default
        if (!label) {
            return 'Fehler';
        }
        return label.innerText;
    }

    static findErrorMessageInShell(shell) {
        const error = shell.querySelector(`[id$="_err"]`);
        return error ? error.innerText : 'Ist ein Pflichtfeld.';
    }

    validate(formGroup, withScrolling = true) {
        this.reset();
        this.collectErrors(formGroup);
        // scroll to fehlerbox
        this.scrollToErrorBox(this.hostElement.nativeElement);
        this.focusFirstItem();
    }

    reset() {
        this.errors = [];
    }

    collectErrors(formGroup: FormGroup, arrayIndex = 0, formGroupName?: string) {
        let counter = 0;
        Object.keys(formGroup.controls).forEach((formControlName) => {
            const ctrl: AbstractControl = formGroup.controls[formControlName];

            if (ctrl.invalid) {
                counter++;
                let targetElement;
                let label;
                let errorMessage;

                // if its FormArray and doesn't have a validator
                if (ctrl instanceof FormArray && (ctrl.validator?.length === 0 || ctrl.validator === null)) {
                    Object.keys(ctrl.controls).forEach((index) => {
                        this.collectErrors(ctrl.controls[index], Number(index));
                    });
                    return null;
                }

                // if its FormGroup
                if (ctrl instanceof FormGroup) {
                    if (this.collectErrors(ctrl, 0, formControlName) !== 0) {
                        return null;
                    }
                }

                if ('customError' in ctrl && (ctrl as unknown as FormControl).errors) {
                    // @ts-ignore
                    this.validatePxErrorFormControlsAndAddToErrors(ctrl);
                    return null;
                }

                // finde die Komponente (formControlName) in einer FormGroup, wenn diese im Template vorhanden ist
                const formGroupInDOM = document.querySelector(`[formgroupname="${formGroupName}"]`);
                let shell = formGroupInDOM
                    ? formGroupInDOM.querySelector(`[formControlName="${formControlName}"]`)
                    : document.querySelectorAll(`[formControlName="${formControlName}"]`)[arrayIndex];
                // finde die Komponente (formArrayName) wenn (formControlName) nicht gefunden
                if (!shell && ctrl instanceof FormArray) {
                    shell = document.querySelector(`[formArrayName="${formControlName}"]`);
                }
                if (shell) {
                    // suche das Label in der Komponente
                    targetElement = ErrorBoxComponent.findTargetElementInShell(shell);
                    label = ErrorBoxComponent.findLabelInShell(shell);
                    errorMessage = ErrorBoxComponent.findErrorMessageInShell(shell);
                }

                this.errors.push({
                    targetElement,
                    label,
                    errorMessage,
                });
            }
        });
        return counter;
    }

    scrollToErrorBox(element: HTMLElement) {
        element.focus({ preventScroll: true });
        element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
        return false;
    }

    scrollToElement(error: PxErrorItem, event?) {
        event?.stopPropagation();
        event?.preventDefault();

        // trigger click event if its set
        if (error.clickEvent) {
            error.clickEvent();
            return;
        }

        const element = error.targetElement;
        element.focus({ preventScroll: true });
        element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
        return false;
    }

    validatePxErrorFormControlsAndAddToErrors(ctrl: PxErrorFormControl) {
        let error: PxErrorItem = {
            label: ctrl.customError.label,
            errorMessage: Object.keys(ctrl.errors).map((key) => ctrl.errors[key])[0],
        };

        if (ctrl.customError.targetElement) {
            error = {
                ...error,
                ...{ targetElement: ctrl.customError.targetElement },
            };
        } else {
            error = {
                ...error,
                ...{ clickEvent: ctrl.customError.clickEvent },
            };
        }

        this.errors.push(error);
    }

    focusFirstItem() {
        const interval = setInterval(() => {
            const firstItem = this.hostElement.nativeElement.querySelector('a');
            if (firstItem) {
                clearInterval(interval);
                firstItem.focus({ preventScroll: true });
            }
        }, 100);
        setTimeout(() => clearInterval(interval), 500);
    }
}
